import React, { FC, useMemo } from 'react'
import classNames from 'classnames'
import { Image } from '@/atoms/Image'
import { slugs } from '@/constants/slugs'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'
import { GiveawayLogo } from '@/views/PreSales/PreSalesGiveawayView/components/Hero/GiveawayLogo'
import { useGiveawayContext } from '@/views/PreSales/PreSalesGiveawayView/context/GiveawayContext'
import { GiveawayTabEnum, GiveawayTab } from '../../config'
import { FooterNavigation } from './FooterNavigation'
import { GiveawayRulesSection } from './GiveawayRulesSection'

interface GiveawayFooterProps {
  scrollToTab: (tab: GiveawayTabEnum) => void
  scrollToProducts: () => void
  tabs: GiveawayTab[]
  isActive: boolean
}

const hiddenTabsOnInActive = new Set([
  GiveawayTabEnum.Details,
  GiveawayTabEnum.Updates,
  GiveawayTabEnum.VacationPackages,
])

export const GiveawayFooter: FC<GiveawayFooterProps> = ({ scrollToTab, scrollToProducts, tabs, isActive }) => {
  const { t } = useTranslate('tickets')
  const { push } = useSafeRouter()
  const { theatricalSlug, giveawayData } = useGiveawayContext()

  const siteNavigation = useMemo(() => {
    const tabNavigation = tabs
      .map((tab) => ({
        name: tab.title,
        onClick: () => scrollToTab(tab.type),
        type: tab.type,
      }))
      .filter((tab) => {
        if (isActive) return true
        return !hiddenTabsOnInActive.has(tab.type)
      })

    return [
      {
        name: isActive ? t('preOrderTickets', 'Pre-Order Tickets') : t('getShowtimes', 'Get Showtimes'),
        onClick: () => (isActive ? scrollToProducts() : push(`/tickets/${theatricalSlug}`)),
      },
      ...tabNavigation,
    ]
  }, [isActive, push, scrollToProducts, scrollToTab, t, tabs, theatricalSlug])

  const getFooterBg = () => {
    if (giveawayData.footerBgImage) {
      return (
        <div className={'absolute inset-0 h-full w-full overflow-hidden bg-giveaway-primary-accent'}>
          <Image
            src={giveawayData.footerBgImage}
            className={`z-10`}
            fill={true}
            style={{ objectFit: 'cover', objectPosition: '50% 50%' }}
            alt={''}
          />
        </div>
      )
    }
    return null
  }
  return (
    <footer
      className={classNames(
        'relative bg-giveaway-primary-accent px-4 pb-[120px] pt-[72px] md:px-[88px] md:py-[72px]',
        theatricalSlug === slugs.iWasAStranger && '!bg-giveaway-primary-accent-dark',
      )}
    >
      {theatricalSlug === slugs.sketch && getFooterBg()}
      <div className="relative inset-0 z-20 mx-auto flex h-full w-full max-w-[1440px] flex-col items-start gap-10 lg:flex-row lg:justify-between xl:gap-[120px]">
        <GiveawayLogo isFooter />
        <GiveawayRulesSection className="gap-2 md:hidden md:gap-3" />
        <FooterNavigation siteNavigation={siteNavigation} />
        <GiveawayRulesSection className="hidden gap-2 md:flex md:gap-3" />
      </div>
    </footer>
  )
}
